/**
 * Production Issues Hooks
 *
 * TanStack Query hooks for production issues data and mutations.
 */
import { useMemo } from 'react';
import { keepPreviousData } from '@tanstack/react-query';
import { trpc } from '@/lib/trpc';
import type { PrintFileOpenCategory } from '@threadheads/shared';
import type {
  IssueType,
  IssueStatus,
  OosFilterState,
  OrderIssueGroup,
} from '../types';

/**
 * Query key factory for issues
 */
export const issueKeys = {
  all: ['production-issues'] as const,
  list: (locationId: string, filters?: { status?: IssueStatus; type?: IssueType }) =>
    [...issueKeys.all, 'list', locationId, filters] as const,
  counts: (locationId: string) => [...issueKeys.all, 'counts', locationId] as const,
  detail: (issueId: string) => [...issueKeys.all, 'detail', issueId] as const,
};

/**
 * Hook to fetch production issues list
 */
export function useProductionIssues(
  locationId: string,
  options?: {
    status?: IssueStatus;
    issueType?: IssueType;
    limit?: number;
    offset?: number;
  }
) {
  return trpc.productionOrders.issues.list.useQuery(
    {
      location_id: locationId,
      status: options?.status,
      issue_type: options?.issueType,
      limit: options?.limit ?? 50,
      offset: options?.offset ?? 0,
    },
    {
      enabled: !!locationId,
    }
  );
}

/**
 * Hook to fetch OOS issues pre-grouped by order, with all filtering and
 * pagination handled server-side (BLU-448).
 *
 * Replaces the legacy pattern of fetching up to 500 rows and applying
 * filter/group/sort/paginate client-side. The legacy path silently hid old
 * issues at any location with > 500 open issues — search included.
 *
 * Maps the backend snake_case payload into the existing frontend
 * `OrderIssueGroup` camelCase shape so `IssueCard` stays unchanged.
 */
export function useOosIssueGroups(
  locationId: string,
  filters: OosFilterState,
  pagination: { limit: number; offset: number }
) {
  const query = trpc.productionOrders.issues.listOosOrderGroups.useQuery(
    {
      location_id: locationId,
      status_filter: filters.status,
      source_filter: filters.source,
      contacted_filter: filters.contactedFilter,
      age_filter: filters.ageFilter,
      search: filters.search || undefined,
      sku_search: filters.skuSearch || undefined,
      sort: filters.sort,
      limit: pagination.limit,
      offset: pagination.offset,
    },
    {
      enabled: !!locationId,
      placeholderData: keepPreviousData,
    }
  );

  const groups: OrderIssueGroup[] = useMemo(() => {
    const raw = query.data?.groups ?? [];
    return raw.map((g) => ({
      prod_order_id: g.prod_order_id,
      order_name: g.order_name ?? undefined,
      customer_display: g.customer_display ?? undefined,
      primaryIssue: g.primary_issue,
      issues: g.issues,
      blockedSkus: g.blocked_skus,
      createdAt: g.created_at,
      hasAwaitingInventory: g.has_awaiting_inventory,
      hasContacted: g.has_contacted,
    }));
  }, [query.data]);

  return {
    ...query,
    groups,
    total: query.data?.total ?? 0,
    truncated: query.data?.truncated ?? false,
  };
}

/**
 * Hook to fetch issue counts for dashboard.
 *
 * `clientOrganizationId` (BLU-817) narrows the counts to one client org at a
 * shared 3PL location — omit it (or pass undefined) for the location-wide
 * counts every OTHER caller of this hook needs (sidebar badge, IssuesPage tab
 * badges, dashboard alert). Passing it forks the React Query cache key into a
 * separate entry; the unfiltered callers are unaffected.
 */
export function useIssueCounts(
  locationId: string,
  options?: { clientOrganizationId?: string }
) {
  return trpc.productionOrders.issues.getCounts.useQuery(
    {
      location_id: locationId,
      client_organization_id: options?.clientOrganizationId,
    },
    {
      enabled: !!locationId,
      refetchInterval: 30000, // Refresh every 30 seconds
    }
  );
}

/**
 * Hook to resolve an issue
 */
export function useResolveIssue() {
  const utils = trpc.useUtils();

  return trpc.productionOrders.issues.resolve.useMutation({
    onSuccess: () => {
      utils.productionOrders.issues.list.invalidate();
      utils.productionOrders.issues.listOosOrderGroups.invalidate();
      utils.productionOrders.issues.getCounts.invalidate();
      // BLU-336: Resolved orders now appear in Ready to Batch tab
      utils.productionOrders.getOrderQueue.invalidate();
      utils.productionOrders.getQueueCounts.invalidate();
    },
  });
}

/**
 * "Accept as-is" for a whole order's ORDER_CHANGED rows — order-scoped so the
 * server resolves every open row, not just the ones on the current page.
 */
export function useAcceptOrderChanges() {
  const utils = trpc.useUtils();

  return trpc.productionOrders.issues.acceptOrderChanges.useMutation({
    onSuccess: () => {
      utils.productionOrders.issues.list.invalidate();
      utils.productionOrders.issues.getCounts.invalidate();
    },
  });
}

/**
 * Hook to route an issue to another location
 */
export function useRouteIssue() {
  const utils = trpc.useUtils();

  return trpc.productionOrders.issues.route.useMutation({
    onSuccess: () => {
      utils.productionOrders.issues.list.invalidate();
      utils.productionOrders.issues.listOosOrderGroups.invalidate();
      utils.productionOrders.issues.getCounts.invalidate();
    },
  });
}

/**
 * Hook to unresolve (reopen) an issue
 */
export function useUnresolveIssue() {
  const utils = trpc.useUtils();

  return trpc.productionOrders.issues.unresolve.useMutation({
    onSuccess: () => {
      utils.productionOrders.issues.list.invalidate();
      utils.productionOrders.issues.listOosOrderGroups.invalidate();
      utils.productionOrders.issues.getCounts.invalidate();
      // BLU-336: Unresolving restores order to queue — refresh Ready tab
      utils.productionOrders.getOrderQueue.invalidate();
      utils.productionOrders.getQueueCounts.invalidate();
    },
  });
}

/**
 * Hook to fetch split order issues for the Split Orders tab.
 * Uses the existing list endpoint filtered to SPLIT_ORDER type.
 */
export function useSplitOrderIssues(
  locationId: string,
  options?: {
    status?: IssueStatus;
    limit?: number;
    offset?: number;
  }
) {
  return trpc.productionOrders.issues.list.useQuery(
    {
      location_id: locationId,
      issue_type: 'SPLIT_ORDER',
      status: options?.status,
      limit: options?.limit ?? 50,
      offset: options?.offset ?? 0,
    },
    {
      enabled: !!locationId,
      refetchInterval: 30000,
      refetchOnWindowFocus: true,
    }
  );
}

/**
 * BLU-563 resolver — load the assignment-board detail for an order, keyed off a
 * prod_order_id (the issues surfaces carry it; the server expands it to the
 * whole order). Powers OrderResolutionFlow.
 */
export function useOrderResolutionDetail(prodOrderId: string | undefined) {
  return trpc.productionOrders.issues.getOrderResolutionDetail.useQuery(
    { prod_order_id: prodOrderId! },
    {
      enabled: !!prodOrderId,
      staleTime: 30000,
    }
  );
}

// ============================================================================
// BLU-695 — order-change resolution (Order Changes tab)
// ============================================================================

/**
 * Every surface an order-change fan-in mutation can move. Both mutations mint
 * or cancel garment units, which changes the issue rows, the tab badges, the
 * live coverage answer AND the batching queue, so all four are invalidated
 * together — the same fan-out `useResolveIssue` does, plus coverage.
 */
function invalidateOrderChangeSurfaces(utils: ReturnType<typeof trpc.useUtils>) {
  utils.productionOrders.issues.list.invalidate();
  utils.productionOrders.issues.listOosOrderGroups.invalidate();
  utils.productionOrders.issues.getCounts.invalidate();
  utils.productionOrders.issues.getOrderCoverage.invalidate();
  utils.productionOrders.getOrderQueue.invalidate();
  utils.productionOrders.getQueueCounts.invalidate();
}

/**
 * BLU-695 — LIVE coverage for one drifted order (hand-over locked decision 1).
 *
 * The resolution card reads THIS, never `prod_issues.metadata`: the stored
 * snapshot is up to a cron cycle stale, and both fan-in mutations re-evaluate
 * coverage server-side before they act. A card quoting the snapshot could state
 * a plan its own Apply then contradicts.
 *
 * `staleTime: 0` is therefore deliberate, not a leftover default — the number
 * on screen has to be the number the server is about to act on.
 */
export function useOrderCoverage(
  prodOrderId: string | undefined,
  options?: { enabled?: boolean }
) {
  return trpc.productionOrders.issues.getOrderCoverage.useQuery(
    { prod_order_id: prodOrderId! },
    {
      enabled: !!prodOrderId && options?.enabled !== false,
      staleTime: 0,
      refetchOnWindowFocus: true,
    }
  );
}

/**
 * Mint the garment units a drifted order is missing (`under`). Order-scoped:
 * the server recomputes the deficit, so the client never sends quantities.
 */
export function useTopUpOrderCoverage() {
  const utils = trpc.useUtils();

  return trpc.productionOrders.issues.topUpOrderCoverage.useMutation({
    onSuccess: () => invalidateOrderChangeSurfaces(utils),
  });
}

/**
 * Cancel the surplus garment units a drifted order no longer needs (`over`).
 * `garment_unit_ids` is re-validated server-side against a fresh coverage read,
 * so the client's selection is UX only; `reason` is mandatory (1 to 500 chars)
 * and is stamped on every cancelled garment.
 */
export function useCancelSurplusUnits() {
  const utils = trpc.useUtils();

  return trpc.productionOrders.issues.cancelSurplusUnits.useMutation({
    onSuccess: () => invalidateOrderChangeSurfaces(utils),
  });
}

/**
 * Hook to fetch print file issues for the Print Files tab.
 * Uses the existing list endpoint filtered to PRINT_FILE_ISSUE type.
 *
 * `category` filters the OPEN view by the server-derived
 * auto-fetching / awaiting-bridge / needs-manual split (only honoured
 * server-side when status='OPEN');
 * `search` narrows by order number / SKU / filename / product name — pass the
 * DEBOUNCED value, this query refires on every input change.
 * `clientOrganizationId` (BLU-817) narrows to one client org at a shared 3PL
 * location — pass the SAME value to `useIssueCounts` so the list and its
 * pager total narrow together.
 */
export function usePrintFileIssues(
  locationId: string,
  options?: {
    status?: IssueStatus;
    category?: PrintFileOpenCategory;
    search?: string;
    clientOrganizationId?: string;
    limit?: number;
    offset?: number;
  }
) {
  return trpc.productionOrders.issues.list.useQuery(
    {
      location_id: locationId,
      issue_type: 'PRINT_FILE_ISSUE',
      status: options?.status,
      category: options?.category,
      search: options?.search,
      client_organization_id: options?.clientOrganizationId,
      limit: options?.limit ?? 50,
      offset: options?.offset ?? 0,
    },
    {
      enabled: !!locationId,
      refetchInterval: 30000,
      refetchOnWindowFocus: true,
    }
  );
}
